import { useCallback, useEffect, useState } from 'react';

import {
  reportsOverview,
  reportsTopBooks,
  type ReportsOverview,
  type TopBook,
} from '../../lib/panelApi';

import { Sparkline } from './Sparkline';

type Props = {
  accessToken: string;
  onGoBooks: () => void;
  onGoUsers: () => void;
  onGoAdmins: () => void;
  onEditBook: (uuid: string) => void;
};

type TopMetric = 'listened' | 'saved' | 'finished';

/**
 * Dashboard / reports landing.
 *
 * Composition:
 *   • Headline counts (books / users / admins / library items).
 *   • DAU / WAU / MAU cards + engagement deltas.
 *   • Sign-ups sparkline (last 30 days).
 *   • Top-books leaderboard switchable between listened / saved /
 *     finished — these are the three signals the catalogue team
 *     actually uses when curating front-page picks.
 *   • Recent admin audit feed for accountability.
 *
 * Why one `reports/overview` call:
 *   Most users land on this page and never drill into a deeper
 *   report; pulling everything in a single round-trip keeps the
 *   initial render under one network spinner.
 */
export function DashboardPage({ accessToken, onGoBooks, onGoUsers, onGoAdmins, onEditBook }: Props) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ReportsOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [topMetric, setTopMetric] = useState<TopMetric>('listened');
  const [top, setTop] = useState<TopBook[]>([]);
  const [topLoading, setTopLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reportsOverview(accessToken);
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const refreshTop = useCallback(
    async (metric: TopMetric) => {
      setTopLoading(true);
      try {
        const res = await reportsTopBooks(accessToken, metric, 10);
        setTop(res.items);
      } catch {
        setTop([]);
      } finally {
        setTopLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    void refresh();
    void refreshTop(topMetric);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  return (
    <div className="card">
      <div className="pageHeader" style={{ marginBottom: 10 }}>
        <div>
          <div className="pageTitle">Dashboard</div>
          <div className="pageSubtitle">
            Engagement, growth, and content health at a glance.
          </div>
        </div>
        <div className="toolbarGroup">
          <button className="btn" onClick={refresh} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error ? (
        <div className="errorBox" style={{ marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      {/* ── totals ──────────────────────────────────────────────── */}
      <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
        <CountCard label="Books"  value={data?.totals.books ?? 0}  onClick={onGoBooks} />
        <CountCard label="Users"  value={data?.totals.users ?? 0}  onClick={onGoUsers} />
        <CountCard label="Admins" value={data?.totals.admins ?? 0} onClick={onGoAdmins} />
        <CountCard label="Library items" value={data?.totals.library_items ?? 0} />
        <CountCard label="Deleted users" value={data?.totals.users_deleted ?? 0} tone="muted" />
      </div>

      {/* ── DAU / WAU / MAU ─────────────────────────────────────── */}
      <div className="item" style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 900, marginBottom: 8 }}>Active users</div>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <Metric label="DAU" value={data?.active.dau ?? 0} hint="24 h" />
          <Metric label="WAU" value={data?.active.wau ?? 0} hint="7 d" />
          <Metric label="MAU" value={data?.active.mau ?? 0} hint="30 d" />
          <Metric
            label="Listened (min/user)"
            value={data?.listening.avg_minutes_per_user.toFixed(1) ?? '0'}
            hint="lifetime"
          />
          <Metric label="Books w/ listeners" value={data?.listening.books_with_listeners ?? 0} />
          <Metric label="Signups 7d" value={data?.engagement.signups_last_7d ?? 0} />
          <Metric label="Signups 30d" value={data?.engagement.signups_last_30d ?? 0} />
          <Metric label="Finished 30d" value={data?.engagement.finished_last_30d ?? 0} />
        </div>
      </div>

      {/* ── signups sparkline ───────────────────────────────────── */}
      <div className="item" style={{ marginTop: 14 }}>
        <div className="row" style={{ alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontWeight: 900 }}>New users (last 30 days)</div>
          <div className="muted" style={{ fontSize: 12 }}>
            Total: {(data?.new_users_30d ?? []).reduce((s, p) => s + p.value, 0)}
          </div>
        </div>
        <Sparkline
          values={(data?.new_users_30d ?? []).map((p) => p.value)}
          labels={(data?.new_users_30d ?? []).map((p) => p.date)}
        />
      </div>

      {/* ── top books leaderboard ───────────────────────────────── */}
      <div className="item" style={{ marginTop: 14 }}>
        <div className="row" style={{ alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontWeight: 900 }}>Top books</div>
          <div className="row" style={{ gap: 4 }}>
            {(['listened', 'saved', 'finished'] as const).map((m) => (
              <button
                key={m}
                className={`pill ${m === topMetric ? 'pill-ok' : ''}`}
                onClick={() => {
                  setTopMetric(m);
                  void refreshTop(m);
                }}
                style={{ cursor: 'pointer' }}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        {topLoading ? (
          <div className="muted">Loading…</div>
        ) : top.length === 0 ? (
          <div className="muted">No data yet.</div>
        ) : (
          <div className="list">
            {top.map((b, i) => (
              <button
                key={b.uuid}
                className="item"
                onClick={() => onEditBook(b.uuid)}
                style={{ textAlign: 'left', cursor: 'pointer', width: '100%' }}
              >
                <div className="row" style={{ gap: 12, alignItems: 'center' }}>
                  <div className="pill" style={{ minWidth: 32, justifyContent: 'center', fontWeight: 900 }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 850 }}>{b.title || 'Untitled'}</div>
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                      {b.authors?.length ? b.authors.join(', ') : '—'}
                    </div>
                  </div>
                  <div className="pill" style={{ minWidth: 90, justifyContent: 'center' }}>
                    <span className="muted" style={{ fontSize: 11 }}>
                      {topMetric === 'listened' ? 'minutes' : 'count'}
                    </span>
                    <span style={{ fontWeight: 900 }}>
                      {topMetric === 'listened' ? Math.round(b.score / 60) : b.score}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── recent audit ────────────────────────────────────────── */}
      <div className="item" style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 900, marginBottom: 8 }}>Recent admin actions</div>
        {(!data || data.recent_audit.length === 0) ? (
          <div className="muted">No admin actions recorded yet.</div>
        ) : (
          <div className="list">
            {data.recent_audit.map((a) => (
              <div className="item" key={a.uuid} style={{ paddingTop: 8, paddingBottom: 8 }}>
                <div style={{ fontWeight: 850 }}>
                  <span className="pill" style={{ marginRight: 6 }}>{a.action}</span>
                  {a.admin_email || 'unknown admin'}
                </div>
                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                  {a.target_kind ? `${a.target_kind} ${a.target_uuid?.slice(0, 8) ?? ''}` : '—'}
                  {a.reason ? ` • ${a.reason}` : ''}
                  {a.ip ? ` • ${a.ip}` : ''}
                  {' • '}
                  {new Date(a.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CountCard({
  label,
  value,
  onClick,
  tone,
}: {
  label: string;
  value: number;
  onClick?: () => void;
  tone?: 'muted';
}) {
  const interactive = !!onClick;
  return (
    <button
      className="btn"
      onClick={onClick}
      disabled={!interactive}
      style={{
        flex: '1 1 160px',
        minWidth: 160,
        padding: '14px',
        borderRadius: 20,
        textAlign: 'left',
        background: 'var(--panel)',
        opacity: tone === 'muted' ? 0.65 : 1,
        cursor: interactive ? 'pointer' : 'default',
      }}
    >
      <div className="muted" style={{ fontSize: 12 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 900, marginTop: 4 }}>{value}</div>
    </button>
  );
}

function Metric({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div
      style={{
        flex: '1 1 140px',
        minWidth: 130,
        padding: '10px 14px',
        borderRadius: 16,
        background: 'var(--surface, #fff)',
        border: '1px solid var(--border, rgba(0,0,0,0.08))',
      }}
    >
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label} {hint ? <span style={{ opacity: 0.6 }}>· {hint}</span> : null}
      </div>
      <div style={{ fontSize: 22, fontWeight: 900, marginTop: 2 }}>{value}</div>
    </div>
  );
}
